import * as React from 'react';
import { cn } from './cn';

/**
 * Table primitives.
 *
 * FleetSight's table: rows straight on the card with no header band, 14px
 * cells, 12px sentence-case column names, and a hairline between rows.
 *
 * The wrapper owns horizontal scrolling, so a wide table never makes the page
 * itself scroll sideways. Header and row-hover use the dedicated tokens
 * (--table-header-bg / --table-row-hover) rather than a hand-picked grey.
 *
 * `stickyHeader` pins the column names. Nineteen rows fit on a laptop and the
 * trip logbook ships sixty, so the names were the first thing to leave the
 * screen and the table became eight unlabelled columns of numbers — the exact
 * moment the density this component exists for stops paying.
 *
 * It is opt-in, and it is opt-in for a reason worth writing down: `position:
 * sticky` resolves against the nearest SCROLLING ancestor, and this wrapper is
 * already one. `overflow-x: auto` computes `overflow-y` to `auto` as well —
 * the spec does not allow one axis to scroll while the other stays visible —
 * so a header set to stick here pins itself to a box whose height is its own
 * content and which therefore never scrolls. The CSS applies, the rule is
 * live in devtools, and nothing whatsoever happens.
 *
 * So the wrapper has to become the vertical scroller too, which means the
 * caller has to give it a height to scroll within: `PageBody fill`, and the
 * card in between carrying `min-h-0 flex-1`. `containerClassName` is how that
 * reaches the wrapper. A `stickyHeader` without the height is the version that
 * looks like it works and does not, so the two travel together.
 *
 * Below `md` every table stacks: each row becomes a card and each cell a
 * label/value line. That horizontal scroll is a correct last resort and a poor
 * first experience — measured across the app, tables ran between 168px and
 * 755px wider than a 390px screen, so most of every row sat off-screen with no
 * affordance saying so.
 *
 * The labels come from the header at runtime rather than from a `data-label`
 * written by hand on all eighteen tables, which would have been a hundred-odd
 * edits to keep in sync with their own headers for ever.
 */

interface TableProps extends React.ComponentProps<'table'> {
  /**
   * Set false where a row is not a record — a matrix, or a layout table whose
   * columns only mean something read against each other.
   */
  stacked?: boolean;
  /**
   * Pin the column names while the rows scroll under them. Requires an
   * ancestor to bound this table's height — see the note above.
   */
  stickyHeader?: boolean;
  /** Reaches the scrolling wrapper, which is where a height bound has to land. */
  containerClassName?: string;
}

function Table({
  className,
  stacked = true,
  stickyHeader = false,
  containerClassName,
  ...props
}: TableProps) {
  const ref = React.useRef<HTMLTableElement>(null);

  // No dependency array on purpose: rows arrive from a query and change shape
  // as often as they change content, and a stale label is worse than the work
  // of two querySelectorAll calls over a table already in the DOM.
  React.useEffect(() => {
    const table = ref.current;
    if (!table || !stacked) return;

    const labels = [...table.querySelectorAll('thead th')].map((th) =>
      (th.textContent ?? '').trim()
    );
    if (labels.length === 0) return;

    for (const row of table.querySelectorAll('tbody tr')) {
      [...row.children].forEach((cell, i) => {
        // An action column has an empty header, and a cell with nothing to
        // announce should print no label rather than an empty one.
        cell.setAttribute('data-label', labels[i] ?? '');
      });
    }
  });

  return (
    <div
      className={cn(
        'relative w-full overflow-x-auto',
        stickyHeader && 'h-full overflow-y-auto',
        containerClassName
      )}
    >
      <table
        ref={ref}
        data-sticky-header={stickyHeader ? 'true' : undefined}
        data-stacked={stacked ? 'true' : undefined}
        className={cn('w-full caption-bottom border-collapse text-base', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      className={cn('[&_tr]:border-b [&_tr]:border-border [&_tr]:hover:bg-transparent', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      className={cn('border-t bg-muted/50 font-medium last:[&>tr]:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-b border-border/70 transition-colors hover:bg-table-row-hover data-[state=selected]:bg-accent/5',
        className
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'bg-table-header px-3 py-2.5 text-left align-middle text-xs font-medium text-muted-foreground first:pl-4 last:pr-4',
        // Pinning is keyed to `data-sticky-header` in base.css, beside the rule
        // that stacks a table on a phone — same idiom, and it keeps a five-part
        // conditional out of the class string.
        'has-[[role=checkbox]]:pr-0',
        className
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      className={cn(
        'px-3 py-2.5 align-middle first:pl-4 last:pr-4 has-[[role=checkbox]]:pr-0',
        className
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption className={cn('mt-3 text-xs text-muted-foreground', className)} {...props} />;
}

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
